import React, { useState, useEffect, useRef, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';

import Flex from 'src/components/flex/Flex';
import SelectLead from 'src/components/input/select-lead/SelectLead';
import { useAuthenticatedState } from 'src/authenticated-state/context';
import type { Option } from 'src/components/input/select/Select';
import { FlexColumn } from 'src/components/flex';

interface Props {
    open: boolean;
    onClose: () => void;
    /**
     * If submitted, the selected coapplicant lead UUIDs will either be empty or include the current lead UUID.
     */
    onSelect: (coapplicantIds: string[]) => void;
    currentLeadUuid: string;
    existingCoapplicantUuids?: string[];
}

export default function SelectCoapplicantModal({ open, onClose, onSelect, currentLeadUuid, existingCoapplicantUuids = [] }: Props) {
    const { leads, loggedInUser } = useAuthenticatedState();
    const [selectedCoapplicants, setSelectedCoapplicants] = useState<string[]>([]);
    const [formValidationError, setFormValidationError] = useState<string | null>(null);
    const prevOpenRef = useRef(open);
    const initialValuesRef = useRef<{ currentLeadUuid: string; existingCoapplicantUuids: string[] } | null>(null);

    useEffect(() => {
        if (open && !prevOpenRef.current) {
            initialValuesRef.current = {
                currentLeadUuid,
                existingCoapplicantUuids: [...existingCoapplicantUuids],
            };
            const initialSelection = [currentLeadUuid, ...existingCoapplicantUuids];
            setSelectedCoapplicants([...new Set(initialSelection)]);
        } else if (!open && prevOpenRef.current) {
            setSelectedCoapplicants([]);
            initialValuesRef.current = null;
        }
        prevOpenRef.current = open;
    }, [open]);

    const getOptionDisabled = useMemo(() => {
        return (option: Option<string | undefined>) => {
            if (!option.value) {
                return false;
            }
            // Allow the current lead and associated coapplicants to be selected
            if (option.value === currentLeadUuid || existingCoapplicantUuids.includes(option.value) || option.value === '__ADD_NEW__') {
                return false;
            }
            // Disable leads that already have a coapplicant group that is different from current lead
            const lead = leads?.find((l) => l.uuid === option.value);
            return lead?.coapplicant_leads_group_id !== null || lead?.owner !== loggedInUser?.id;
        };
    }, [leads, currentLeadUuid, existingCoapplicantUuids, loggedInUser?.id]);

    const handleSubmit = () => {
        let leadUuidsToSubmit: string[];
        if (selectedCoapplicants.length === 0) {
            leadUuidsToSubmit = [];
        } else if (selectedCoapplicants.includes(currentLeadUuid)) {
            leadUuidsToSubmit = selectedCoapplicants;
        } else {
            leadUuidsToSubmit = [currentLeadUuid, ...selectedCoapplicants];
        }

        if (leadUuidsToSubmit.length === 1 && leadUuidsToSubmit[0] === currentLeadUuid) {
            setFormValidationError('Please select at least one co-applicant along with the current lead.');
            return false;
        } else {
            setFormValidationError(null);
        }

        onSelect(leadUuidsToSubmit);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        width: '500px',
                        maxWidth: '500px',
                        height: '300px',
                        maxHeight: '300px',
                    }
                }
            }}
        >
            <DialogTitle>
                <Flex justifyContent="space-between" alignItems="center">
                    {existingCoapplicantUuids.length > 0 ? 'Edit co-applicant(s)' : 'Add co-applicant(s)'}
                    <IconButton onClick={onClose} edge="end" size="small">
                        <CloseIcon />
                    </IconButton>
                </Flex>
            </DialogTitle>
            <DialogContent sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <FlexColumn gap={1}>
                    {formValidationError ? <Alert severity="error">{formValidationError}</Alert> : null}
                    <SelectLead
                        value={selectedCoapplicants}
                        allowMultiple={true}
                        onChange={(value) => {
                            setSelectedCoapplicants(value || []);
                            setFormValidationError(null);
                        }}
                        getOptionDisabled={getOptionDisabled}
                    />
                </FlexColumn>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleSubmit} variant="contained" disabled={selectedCoapplicants.length > 0 && !selectedCoapplicants.includes(currentLeadUuid)}>
                    {existingCoapplicantUuids.length > 0 ? 'Save' : 'Add'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}